import { z } from 'zod';
import {
  DENSITIES,
  EMOJI_NAME_PATTERN,
  MESSAGING_LIMITS,
  SIDEBAR_SORTS,
  THEMES,
  channelKindSchema,
  conversationMemberRoleSchema,
  desktopNotifyLevelSchema,
  listedConversationKindSchema,
  messageKindSchema,
  mobileNotifyLevelSchema,
  notifyLevelSchema,
  postingPolicySchema,
  presenceModeSchema,
  presenceStateSchema,
} from '../enums';
import {
  TASK_EVENT_PREFERENCE_KEYS,
  pushPreviewSchema,
  readReceiptsSettingSchema,
} from '../enums/notifications';
import { richTextDocWireSchema } from '../richtext/schema';
import { taskKindSchema, taskStatusSchema } from '../tasks';
import { meDtoSchema } from './auth';
import {
  commaList,
  isoDateTimeSchema,
  localDateSchema,
  multiLineText,
  queryBooleanSchema,
  singleLineText,
  uuidSchema,
} from './common';
import { fileDtoSchema } from './files';
import { bookmarkDtoSchema } from './bookmarks';
import { pageInfoSchema, paginated, paginationQuerySchema } from './pagination';
import { sidebarSectionDtoSchema } from './sidebar';

/**
 * Messaging contracts (PLAN §4 "Later modules — Messaging", Phase 1 rows; D-015, D-045, D-109).
 * Events reuse these DTOs (PLAN §5 "Shared payload types"), so an event never carries a field the
 * REST API would not show that recipient. Owned by the messaging workstream from Phase 1 on.
 *
 * Path parameters are named `conversationId`, `messageId`, `userId` and `emoji` (the PLAN tables
 * write `:id`). Seq anchors are integers; `seq` is per conversation and gap-free (D-015).
 */

// ================================================================ shared pieces

export const conversationIdParamsSchema = z.object({ conversationId: uuidSchema });
export type ConversationIdParams = z.infer<typeof conversationIdParamsSchema>;

export const messageIdParamsSchema = z.object({ messageId: uuidSchema });
export type MessageIdParams = z.infer<typeof messageIdParamsSchema>;

export const conversationMemberParamsSchema = z.object({
  conversationId: uuidSchema,
  userId: uuidSchema,
});
export type ConversationMemberParams = z.infer<typeof conversationMemberParamsSchema>;

export const emojiNameSchema = z.string().regex(EMOJI_NAME_PATTERN, 'Unknown emoji name');

export const reactionParamsSchema = z.object({ messageId: uuidSchema, emoji: emojiNameSchema });
export type ReactionParams = z.infer<typeof reactionParamsSchema>;

/** A conversation sequence number (D-015). */
export const seqSchema = z.number().int().nonnegative();
const querySeq = z.coerce.number().int().nonnegative();

/**
 * `Idempotency-Key` of message send: a UUIDv4 or UUIDv7 the client generates; it becomes the
 * message's `clientMsgId` (D-015). Missing → `400 idempotency_key_required`, malformed →
 * `400 idempotency_key_invalid`.
 */
export const messageIdempotencyKeySchema = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[47][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i);

/** Generic `Idempotency-Key` for other creating POSTs (PLAN §4 "Idempotency"). */
export const idempotencyKeySchema = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);

// ================================================================ DTOs

/** The viewer's own state in a conversation (`conversation_members`). */
export const memberStateDtoSchema = z.object({
  conversationId: uuidSchema,
  joinedAt: isoDateTimeSchema,
  role: conversationMemberRoleSchema,
  lastReadSeq: seqSchema,
  /** Incremented by every read or unread write; apply `read.updated` only when newer. */
  readVersion: z.number().int().nonnegative(),
  /** DMs and group DMs only (channels are bold instead), capped at 100 (the UI shows 99+). */
  unreadCount: z.number().int().min(0).max(MESSAGING_LIMITS.counterCap),
  mentionCount: z.number().int().min(0).max(MESSAGING_LIMITS.counterCap),
  /** `default` = the member's own default (`notificationPreferences.desktopLevel`). */
  notifyLevel: notifyLevelSchema,
  /** Phase 3 (mobile). */
  mobileNotifyLevel: notifyLevelSchema,
  muted: z.boolean(),
  mutedUntil: isoDateTimeSchema.nullable(),
  /** Phase 2 (the Starred section). */
  starred: z.boolean(),
  /** Phase 2 (custom sidebar sections). */
  sectionId: uuidSchema.nullable(),
  hidden: z.boolean(),
  lastViewedAt: isoDateTimeSchema.nullable(),
  version: z.number().int().positive(),
});
export type MemberStateDto = z.infer<typeof memberStateDtoSchema>;

/**
 * The last line of a conversation, for the rows that preview it (D-1213).
 *
 * Direct conversations only. A channel's list row is its name — one line of the hundreds a busy
 * channel carries tells you nothing and costs a query per channel to find out — while a DM IS its
 * last line, which is why every messaging app in the world shows it and why a DM list without it
 * is a list of names you have to open one by one.
 *
 * `text` is the server's own plain text (the same derivation search and notifications use),
 * truncated: a row shows one line and a 4,000-character message would be sent in full to draw 60
 * of them. It is `''` for a message that is only a file, which is what `hasFile` is for — the row
 * says "Sent a file" rather than drawing an empty line.
 */
export const conversationPreviewSchema = z.object({
  /** Who wrote it. Null for a message the product itself posted (the system user has no profile). */
  authorId: uuidSchema.nullable(),
  text: z.string(),
  hasFile: z.boolean(),
  at: isoDateTimeSchema,
});
export type ConversationPreview = z.infer<typeof conversationPreviewSchema>;

/** How much of a message a list row is sent (D-1213). One line, at any width. */
export const CONVERSATION_PREVIEW_MAX_CHARS = 140;

export const conversationDtoSchema = z.object({
  id: uuidSchema,
  /** The hidden `task` kind is never listed (D-054). */
  kind: listedConversationKindSchema,
  /** Channels only; lowercase, ≤ 80. */
  name: z.string().nullable(),
  topic: z.string().nullable(),
  description: z.string().nullable(),
  createdBy: uuidSchema,
  createdAt: isoDateTimeSchema,
  archivedAt: isoDateTimeSchema.nullable(),
  /** The workspace-wide channel: cannot be left, archived or made private. */
  isGeneral: z.boolean(),
  /** New non-guest members join on acceptance (`/admin/default-channels`). */
  autoJoin: z.boolean(),
  postingPolicy: postingPolicySchema,
  memberCount: z.number().int().nonnegative(),
  /** Newest seq of any message, thread replies included (gap detection, D-015). */
  lastSeq: seqSchema,
  /** Newest channel-visible seq (roots and replies sent to the channel): bold when > lastReadSeq. */
  lastChannelSeq: seqSchema,
  lastMessageAt: isoDateTimeSchema.nullable(),
  /**
   * The last line, for a DM list row (D-1213). Null when the conversation has nothing in it, and
   * ABSENT for a channel and from a server that predates this — a reader must treat "not sent"
   * and "nothing to show" the same way, which is why it is optional rather than required-null.
   */
  lastMessage: conversationPreviewSchema.nullish(),
  /** DM and group DM participants other than the viewer; `[]` for channels and the self-DM. */
  dmUserIds: z.array(uuidSchema),
  /**
   * Set when a private channel was made public keeping earlier history members-only (D-109):
   * non-members read only `seq >= publicSinceSeq`.
   */
  publicSinceSeq: seqSchema.nullable(),
  version: z.number().int().positive(),
  membersVersion: z.number().int().nonnegative(),
  /** Null when the viewer is not a member (a public channel preview). */
  memberState: memberStateDtoSchema.nullable(),
  /** Phase 6. */
  huddle: z.object({ active: z.boolean(), participantIds: z.array(uuidSchema) }).nullable(),
});
export type ConversationDto = z.infer<typeof conversationDtoSchema>;

export const reactionDtoSchema = z.object({
  /** A standard shortcode (`+1`) or a custom emoji name, with an optional skin tone. */
  emoji: z.string(),
  count: z.number().int().positive(),
  /** At most 50; the full list is `GET /messages/:messageId/reactions`. */
  userIds: z.array(uuidSchema),
});
export type ReactionDto = z.infer<typeof reactionDtoSchema>;

/** On thread roots. `following` and `lastReadSeq` are the viewer's (neutral values in broadcasts). */
export const threadSummaryDtoSchema = z.object({
  replyCount: z.number().int().nonnegative(),
  lastReplyAt: isoDateTimeSchema.nullable(),
  lastReplySeq: seqSchema.nullable(),
  /** At most 5, newest first. */
  recentReplierIds: z.array(uuidSchema),
  following: z.boolean(),
  lastReadSeq: seqSchema.nullable(),
});
export type ThreadSummaryDto = z.infer<typeof threadSummaryDtoSchema>;

/** Server-derived from the body; clients never send mentions. */
export const messageMentionsDtoSchema = z.object({
  userIds: z.array(uuidSchema),
  groupIds: z.array(uuidSchema),
  /** `#channel` links. */
  conversationIds: z.array(uuidSchema),
  here: z.boolean(),
  channel: z.boolean(),
});
export type MessageMentionsDto = z.infer<typeof messageMentionsDtoSchema>;

/**
 * One link preview on a message (`message_link_previews` + `link_previews`). `id` is the
 * `link_previews.id` (the `:unfurlId` of `DELETE /messages/:id/unfurls/:unfurlId`); the image is
 * re-hosted as a file, never a third-party URL.
 */
export const unfurlDtoSchema = z.object({
  id: uuidSchema,
  url: z.string(),
  siteName: z.string().nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  imageFileId: uuidSchema.nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
});
export type UnfurlDto = z.infer<typeof unfurlDtoSchema>;

/**
 * A task chip on its source message (PLAN §3.2, D-037). Viewers who can see the task get every
 * field; everyone else who can read the message gets only `number` and `status` (the rest null).
 * Live changes arrive as `task.chip_updated` on the message's conversation.
 */
export const taskChipDtoSchema = z.object({
  taskId: uuidSchema.nullable(),
  /** `T-1042`. */
  number: z.string(),
  kind: taskKindSchema,
  status: taskStatusSchema,
  ownerUserId: uuidSchema.nullable(),
  dueDate: localDateSchema.nullable(),
});
export type TaskChipDto = z.infer<typeof taskChipDtoSchema>;

export const messageDtoSchema = z.object({
  id: uuidSchema,
  conversationId: uuidSchema,
  /** Per-conversation, gap-free, increasing (D-015). */
  seq: seqSchema,
  threadRootId: uuidSchema.nullable(),
  alsoSentToChannel: z.boolean(),
  kind: messageKindSchema,
  /** For system and card messages, e.g. `member_joined`. */
  subtype: z.string().nullable(),
  /** `users.id` of the person, bot user or the workspace's system user (D-049). */
  authorId: uuidSchema,
  /** Phase 2 (bot and incoming-webhook posts). */
  botId: uuidSchema.nullable(),
  authorOverride: z
    .object({ displayName: z.string().nullable(), iconEmoji: z.string().nullable() })
    .nullable(),
  /** ProseMirror JSON (D-045); null when deleted and for system messages rendered from metadata. */
  content: richTextDocWireSchema.nullable(),
  /** Server-derived plain text; `''` when deleted. */
  plainText: z.string(),
  mentions: messageMentionsDtoSchema,
  /** `[]` when deleted. */
  attachments: z.array(fileDtoSchema),
  /** Phase 2 (link unfurls): ready, non-dismissed previews in position order. */
  unfurls: z.array(unfurlDtoSchema),
  reactions: z.array(reactionDtoSchema),
  /** Roots only (null on replies and on roots that never had a reply). */
  thread: threadSummaryDtoSchema.nullable(),
  /** Phase 2. */
  pinned: z.boolean(),
  /** Phase 2. */
  forwardedFrom: z
    .object({
      messageId: uuidSchema,
      conversationId: uuidSchema,
      authorId: uuidSchema.nullable(),
      textSnapshot: z.string(),
      createdAt: isoDateTimeSchema,
    })
    .nullable(),
  /** Phase 4: tasks created from this message (PLAN §3.2 "Task chip on messages"). */
  taskChips: z.array(taskChipDtoSchema),
  /** Subtype-specific payload (`messages.metadata`). */
  metadata: z.record(z.string(), z.unknown()),
  /** The sender's retry key; the originating tab merges its optimistic bubble by it. */
  clientMsgId: uuidSchema.nullable(),
  createdAt: isoDateTimeSchema,
  editedAt: isoDateTimeSchema.nullable(),
  deletedAt: isoDateTimeSchema.nullable(),
  /** Bumped by every edit, delete, reaction and thread-summary change: apply only if newer. */
  version: z.number().int().positive(),
});
export type MessageDto = z.infer<typeof messageDtoSchema>;

export const badgeDtoSchema = z.object({
  /** mentions + dms + threads (the Activity count is separate: it would double-count mentions). */
  total: z.number().int().nonnegative(),
  mentions: z.number().int().nonnegative(),
  dms: z.number().int().nonnegative(),
  threads: z.number().int().nonnegative(),
  activityUnread: z.number().int().nonnegative(),
  /**
   * Phase 3 (D-508): tasks waiting for my acceptance and decision requests waiting for me. They
   * badge the mobile Tasks tab (and the web task nav); they are not part of `total`.
   */
  tasksAwaitingMe: z.number().int().nonnegative(),
  decisionsForMe: z.number().int().nonnegative(),
  /**
   * D-1147: unsent work of the caller's — saved drafts plus messages still waiting to go out.
   * It badges "Drafts & sent" in the sidebar, the way Slack counts its pencil.
   *
   * NOT part of `total`, and deliberately not a notification: nothing here is somebody waiting on
   * you, it is you waiting on yourself. `total` is what the app icon and the tab title show, and
   * a draft you left open is not a reason to make the product look unread.
   */
  drafts: z.number().int().nonnegative(),
  /** Redis TIME in microseconds; apply only when higher than the last one applied. */
  version: z.number().int().positive(),
});
export type BadgeDto = z.infer<typeof badgeDtoSchema>;

// ================================================================ preferences

/** `/me/notification-preferences` (SPEC §4.6). Mobile and email parts are used from Phase 3. */
export const notificationPreferencesDtoSchema = z.object({
  desktopLevel: desktopNotifyLevelSchema,
  threadReplies: z.boolean(),
  muteAllSounds: z.boolean(),
  mobile: z.object({
    level: mobileNotifyLevelSchema,
    /** Null = the workspace default `notifications.pushDelaySeconds`. */
    pushDelaySeconds: z.number().int().nullable(),
    /**
     * Phase 3 (D-505): what a push shows — `full`, `sender_only` or `none`. Null = the workspace
     * default `notifications.pushPreviewDefault`.
     */
    pushPreview: pushPreviewSchema.nullable(),
  }),
  email: z.object({
    digestEnabled: z.boolean(),
    /** Null = `notifications.emailDigestDefaultHours`. */
    digestAfterHours: z.number().int().nullable(),
  }),
  /** Phase 2: keyword notifications, stored normalised (lower-case, NFC, accents folded). */
  keywords: z.array(z.string()),
  /**
   * Phase 3 (D-514): task, decision and PTSTDM notification toggles
   * (`notification_preferences.task_events`). Every key is present; a key never stored is on.
   */
  taskEvents: z.record(z.enum(TASK_EVENT_PREFERENCE_KEYS), z.boolean()),
});
export type NotificationPreferencesDto = z.infer<typeof notificationPreferencesDtoSchema>;

const pushDelaySchema = z.union([
  z.literal(0),
  z.literal(60),
  z.literal(120),
  z.literal(300),
  z.literal(600),
]);

/** PATCH /me/notification-preferences. */
export const updateNotificationPreferencesRequestSchema = z.strictObject({
  desktopLevel: desktopNotifyLevelSchema.optional(),
  threadReplies: z.boolean().optional(),
  muteAllSounds: z.boolean().optional(),
  mobile: z
    .strictObject({
      level: mobileNotifyLevelSchema.optional(),
      pushDelaySeconds: pushDelaySchema.nullable().optional(),
      /** Phase 3: null returns to the workspace default. */
      pushPreview: pushPreviewSchema.nullable().optional(),
    })
    .optional(),
  email: z
    .strictObject({
      digestEnabled: z.boolean().optional(),
      digestAfterHours: z.number().int().min(1).max(24).nullable().optional(),
    })
    .optional(),
  /** Phase 2: replaces the list; each 2–50 characters, at most 50, duplicates collapse. */
  keywords: z.array(singleLineText(2, 50)).max(50).optional(),
  /** Phase 3: merged into the stored toggles (keys not sent keep their value). */
  taskEvents: z.partialRecord(z.enum(TASK_EVENT_PREFERENCE_KEYS), z.boolean()).optional(),
});
export type UpdateNotificationPreferencesRequest = z.infer<
  typeof updateNotificationPreferencesRequestSchema
>;

/**
 * A chord written down (D-1218): `mod`, `alt` and `shift` in that order, then the key, joined by
 * `+` — `mod+shift+l`, `alt+ArrowUp`, `Escape`. `mod` is Cmd on a Mac and Ctrl everywhere else,
 * exactly as the web's `Chord` means it, so one stored value is right on every platform.
 */
const CHORD_KEY =
  /^(?:[A-Za-z0-9]|[/?.,;'`\-=\\[\]]|Arrow(?:Up|Down|Left|Right)|Escape|Enter|Home|End|Page(?:Up|Down)|F[1-9]|F1[0-2])$/;
const CHORD_MODIFIERS = ['mod', 'alt', 'shift'] as const;

export function isChordString(value: string): boolean {
  const parts = value.split('+');
  const key = parts.pop();
  if (!key || !CHORD_KEY.test(key)) return false;
  // Each modifier at most once, in the order above, so one chord has exactly one spelling.
  let next = 0;
  for (const part of parts) {
    const at = CHORD_MODIFIERS.indexOf(part as (typeof CHORD_MODIFIERS)[number]);
    if (at < next) return false;
    next = at + 1;
  }
  return true;
}

export const chordStringSchema = z
  .string()
  .max(40)
  .refine(isChordString, 'That is not a key combination.');

/** How many shortcuts one person may have changed: the list the client offers is far shorter. */
export const MAX_SHORTCUT_OVERRIDES = 64;

/** `/me/preferences`: UI preferences that sync across devices (stored in `workspace_members.ui_prefs`). */
export const uiPreferencesDtoSchema = z.object({
  theme: z.enum(THEMES),
  density: z.enum(DENSITIES),
  composerToolbar: z.boolean(),
  sidebarShowUnreadsOnly: z.boolean(),
  sidebarSort: z.enum(SIDEBAR_SORTS),
  /** Enter sends (Shift+Enter for a new line); false swaps them. */
  enterSends: z.boolean(),
  /**
   * Phase 3 (D-550): share my read position as read receipts. Reciprocal: off also hides other
   * people's receipts from me. Stored in `workspace_members.share_read_receipts` (the server
   * enforces it), not in `ui_prefs`.
   */
  shareReadReceipts: z.boolean(),
  /**
   * D-1218: the keyboard chords this person has changed, as `{ [shortcutId]: "mod+shift+l" }`.
   *
   * Only what DIFFERS from the default is stored. A shortcut nobody has touched keeps following
   * the product, so the day a default has to move (D-1164 moved one because Chrome ate it) it
   * moves for everybody who never had an opinion — which is almost everybody. The server keeps
   * the map without knowing the ids: the client owns the list of shortcuts, and an id it does not
   * recognise is ignored rather than refused, so an older tab cannot lose somebody's settings.
   */
  shortcuts: z
    .record(z.string().min(1).max(48), chordStringSchema)
    .refine(
      (map) => Object.keys(map).length <= MAX_SHORTCUT_OVERRIDES,
      `At most ${MAX_SHORTCUT_OVERRIDES} shortcuts can be changed.`,
    ),
});
export type UiPreferencesDto = z.infer<typeof uiPreferencesDtoSchema>;

export const DEFAULT_UI_PREFERENCES: UiPreferencesDto = Object.freeze({
  theme: 'system',
  density: 'comfortable',
  composerToolbar: true,
  sidebarShowUnreadsOnly: false,
  sidebarSort: 'alpha',
  enterSends: true,
  shareReadReceipts: true,
  shortcuts: Object.freeze({}),
});

export const updateUiPreferencesRequestSchema = uiPreferencesDtoSchema.partial().strict();
export type UpdateUiPreferencesRequest = z.infer<typeof updateUiPreferencesRequestSchema>;

// ================================================================ bootstrap and realtime tickets

/** GET /me/bootstrap — everything for the first render and for socket resume. */
export const bootstrapResponseSchema = z.object({
  me: meDtoSchema,
  /** Every conversation the caller is an active member of (never `task`), each with `memberState`. */
  conversations: z.array(conversationDtoSchema),
  /**
   * Phase 2: my sidebar sections (custom ones and the rows storing system sections' state), in
   * `position` order — the same shape as `GET /sidebar/sections` (`SidebarSectionDto`).
   */
  sections: z.array(sidebarSectionDtoSchema),
  preferences: uiPreferencesDtoSchema,
  notificationPreferences: notificationPreferencesDtoSchema,
  /** Phase 2 caches; 0 until then. */
  customEmojiVersion: z.number().int().nonnegative(),
  userGroupsVersion: z.number().int().nonnegative(),
  activityUnreadCount: z.number().int().nonnegative(),
  badge: badgeDtoSchema,
  /**
   * Resume point for the socket (D-075). The API reads the cursor (Redis TIME − 1 s) before it
   * reads the database, so the snapshot and the replay overlap instead of leaving a hole.
   */
  realtime: z.object({ epoch: z.string(), cursor: z.string() }),
  /**
   * The workspace limits the composer checks before starting work (the server still enforces
   * them): `messaging.maxUploadBytes`.
   */
  limits: z.object({ maxUploadBytes: z.number().int().positive() }),
  /**
   * Phase 3 (D-550): the workspace's `messaging.readReceipts`, so clients decide with
   * `readReceiptsVisibility` (@orbit/shared/read-receipts) whether to ask for receipts at all.
   */
  readReceipts: z.object({ setting: readReceiptsSettingSchema }),
  serverTime: isoDateTimeSchema,
});
export type BootstrapResponse = z.infer<typeof bootstrapResponseSchema>;

/** POST /realtime/tickets — watch a conversation the caller is not a member of (D-077). */
export const createRealtimeTicketRequestSchema = z.strictObject({ conversationId: uuidSchema });
export type CreateRealtimeTicketRequest = z.infer<typeof createRealtimeTicketRequestSchema>;

export const realtimeTicketResponseSchema = z.object({
  /** `base64url({uid, wid, cid, exp})` `.` HMAC-SHA256; valid 60 s. */
  ticket: z.string(),
  expiresAt: isoDateTimeSchema,
});
export type RealtimeTicketResponse = z.infer<typeof realtimeTicketResponseSchema>;

// ================================================================ conversations

/** GET /conversations — my conversations for the sidebar (limit up to 500). */
export const listConversationsQuerySchema = z.object({
  kinds: commaList(listedConversationKindSchema).optional(),
  includeArchived: queryBooleanSchema.default(false),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(500),
});
export type ListConversationsQuery = z.infer<typeof listConversationsQuerySchema>;

export const conversationListResponseSchema = paginated(conversationDtoSchema);
export type ConversationListResponse = z.infer<typeof conversationListResponseSchema>;

/** GET /conversations/browse — the channel browser (member+; guests 403). */
export const browseChannelsQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(1).max(100).optional(),
  sort: z.enum(['name', 'members', 'created', 'activity']).default('name'),
  joined: z.enum(['any', 'yes', 'no']).default('any'),
  includeArchived: queryBooleanSchema.default(false),
});
export type BrowseChannelsQuery = z.infer<typeof browseChannelsQuerySchema>;

export const browsedChannelDtoSchema = conversationDtoSchema.extend({ isMember: z.boolean() });
export type BrowsedChannelDto = z.infer<typeof browsedChannelDtoSchema>;
export const browseChannelsResponseSchema = paginated(browsedChannelDtoSchema);
export type BrowseChannelsResponse = z.infer<typeof browseChannelsResponseSchema>;

/**
 * Channel name as typed: trimmed, NFC, lowercased by the server. The pattern
 * (`CHANNEL_NAME_PATTERN`) is checked by the service so a bad name is
 * `422 invalid_channel_name`, not a 400.
 */
export const channelNameInputSchema = z.string().trim().normalize('NFC').min(1).max(80);

/** POST /conversations — create a channel (member+, `messaging.channelCreationRoles`). */
export const createChannelRequestSchema = z.strictObject({
  kind: channelKindSchema,
  name: channelNameInputSchema,
  topic: singleLineText(0, MESSAGING_LIMITS.maxTopicChars).optional(),
  description: multiLineText(0, MESSAGING_LIMITS.maxDescriptionChars).optional(),
  memberUserIds: z.array(uuidSchema).max(500).optional(),
});
export type CreateChannelRequest = z.infer<typeof createChannelRequestSchema>;

/**
 * POST /conversations/dm — get or create: 0 others = self-DM, 1 = DM, 2–8 = group DM (≤ 9 people
 * including the creator). `200` existing, `201` new. Guests only with people they share a
 * conversation with (others answer `404 member_not_found`).
 */
export const openDmRequestSchema = z.strictObject({
  userIds: z.array(uuidSchema).max(MESSAGING_LIMITS.groupDmMaxMembers - 1),
});
export type OpenDmRequest = z.infer<typeof openDmRequestSchema>;

/** GET /conversations/:conversationId — conv-read. */
export const conversationDetailResponseSchema = conversationDtoSchema.extend({
  /** Phase 2 (pins). */
  pinnedCount: z.number().int().nonnegative(),
  /** Phase 2 (bookmarks bar), in `position` order. */
  bookmarks: z.array(bookmarkDtoSchema),
});
export type ConversationDetailResponse = z.infer<typeof conversationDetailResponseSchema>;

/** PATCH /conversations/:conversationId — each change posts a system message. */
export const updateConversationRequestSchema = z.strictObject({
  name: channelNameInputSchema.optional(),
  topic: singleLineText(0, MESSAGING_LIMITS.maxTopicChars).nullable().optional(),
  description: multiLineText(0, MESSAGING_LIMITS.maxDescriptionChars).nullable().optional(),
  /**
   * D-1144: who may post. `admins_only` is the announcement channel — everyone reads, admins and
   * the channel's managers write.
   *
   * `conversations.posting_policy` has existed since Phase 1 and `conversationCapabilities` has
   * always honoured it (permissions.ts), but nothing could ever SET it: it was not in this body
   * nor in the create request, so every channel in every workspace was `everyone` for ever. The
   * rule was enforced and unreachable.
   */
  postingPolicy: postingPolicySchema.optional(),
});
export type UpdateConversationRequest = z.infer<typeof updateConversationRequestSchema>;

/** POST /conversations/:conversationId/convert — privacy change (D-109). */
export const convertConversationRequestSchema = z.strictObject({
  to: channelKindSchema,
  /** The channel name typed exactly (`422 confirmation_mismatch`). */
  confirmName: z.string().trim().min(1).max(80),
  /** Private → public only: keep earlier history members-only (`public_since_seq`). */
  keepEarlierHistoryPrivate: z.boolean().optional(),
});
export type ConvertConversationRequest = z.infer<typeof convertConversationRequestSchema>;

/** GET /conversations/:conversationId/members — conv-read. */
export const listConversationMembersQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(1).max(100).optional(),
});
export type ListConversationMembersQuery = z.infer<typeof listConversationMembersQuerySchema>;

export const conversationMemberDtoSchema = z.object({
  userId: uuidSchema,
  role: conversationMemberRoleSchema,
  joinedAt: isoDateTimeSchema,
  addedByUserId: uuidSchema.nullable(),
});
export type ConversationMemberDto = z.infer<typeof conversationMemberDtoSchema>;
export const conversationMemberListResponseSchema = paginated(conversationMemberDtoSchema);
export type ConversationMemberListResponse = z.infer<typeof conversationMemberListResponseSchema>;

/** POST /conversations/:conversationId/members — conv-member; adding guests needs admin+. */
export const addConversationMembersRequestSchema = z.strictObject({
  userIds: z.array(uuidSchema).min(1).max(100),
});
export type AddConversationMembersRequest = z.infer<typeof addConversationMembersRequestSchema>;

export const addConversationMembersResponseSchema = z.object({
  added: z.array(uuidSchema),
  alreadyMembers: z.array(uuidSchema),
});
export type AddConversationMembersResponse = z.infer<typeof addConversationMembersResponseSchema>;

/** PUT /conversations/:conversationId/members/:userId/role — admin+ or a channel manager. */
export const setConversationMemberRoleRequestSchema = z.strictObject({
  role: conversationMemberRoleSchema,
});
export type SetConversationMemberRoleRequest = z.infer<
  typeof setConversationMemberRoleRequestSchema
>;

export const setConversationMemberRoleResponseSchema = z.object({
  userId: uuidSchema,
  role: conversationMemberRoleSchema,
});

/**
 * PATCH /conversations/:conversationId/member-state — my settings for this conversation.
 * `mutedUntil: null` unmutes; `9999-12-31T00:00:00.000Z` means until unmuted.
 */
export const updateMemberStateRequestSchema = z.strictObject({
  notifyLevel: notifyLevelSchema.optional(),
  mobileNotifyLevel: notifyLevelSchema.optional(),
  mutedUntil: isoDateTimeSchema.nullable().optional(),
  /** Phase 2 (the Starred section). */
  starred: z.boolean().optional(),
  /** Phase 2: one of my custom sidebar sections, or null for the default group (D-176). */
  sectionId: uuidSchema.nullable().optional(),
  /** Close a DM or group DM from the sidebar; the next incoming message reopens it. */
  hidden: z.boolean().optional(),
});
export type UpdateMemberStateRequest = z.infer<typeof updateMemberStateRequestSchema>;

/** The `mutedUntil` value that means "muted until unmuted". */
export const MUTED_FOREVER = '9999-12-31T00:00:00.000Z';

/** GET, PUT /admin/default-channels — admin+ (public channels only). */
export const defaultChannelsDtoSchema = z.object({ conversationIds: z.array(uuidSchema) });
export type DefaultChannelsDto = z.infer<typeof defaultChannelsDtoSchema>;
export const setDefaultChannelsRequestSchema = z.strictObject({
  conversationIds: z.array(uuidSchema).max(50),
});
export type SetDefaultChannelsRequest = z.infer<typeof setDefaultChannelsRequestSchema>;

// ================================================================ messages

/**
 * GET /conversations/:conversationId/messages — channel-level history (roots plus replies sent
 * to the channel), ascending seq. With no anchor the latest page. At most one of the anchors
 * (`400 anchors_conflict`); `aroundSeq` returns limit/2 on each side, inclusive. `afterSeq` is the
 * gap-fill mode: it returns EVERY row after the seq in seq order, thread replies and tombstones
 * included (PLAN §5 "Gap detection and REST backfill").
 */
export const listMessagesQuerySchema = z.object({
  beforeSeq: querySeq.optional(),
  afterSeq: querySeq.optional(),
  aroundSeq: querySeq.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /** Continues in the anchor's direction (from `nextCursor`). */
  cursor: z.string().min(1).optional(),
});
export type ListMessagesQuery = z.infer<typeof listMessagesQuerySchema>;

export const messageListResponseSchema = z.object({
  items: z.array(messageDtoSchema),
  hasMoreBefore: z.boolean(),
  hasMoreAfter: z.boolean(),
  /** `conversations.last_seq` when the page was read. */
  lastSeq: seqSchema,
  nextCursor: z.string().nullable(),
  page: pageInfoSchema.optional(),
});
export type MessageListResponse = z.infer<typeof messageListResponseSchema>;

/** GET /conversations/:conversationId/sync — refresh a cached conversation. */
export const syncConversationQuerySchema = z.object({
  afterSeq: querySeq,
  updatedSince: isoDateTimeSchema.optional(),
});
export type SyncConversationQuery = z.infer<typeof syncConversationQuerySchema>;

export const syncConversationResponseSchema = z.object({
  /** Every seq > afterSeq, thread replies included (≤ 500). */
  messages: z.array(messageDtoSchema),
  /** Messages whose version changed since `updatedSince` (≤ 500). */
  updated: z.array(messageDtoSchema),
  memberState: memberStateDtoSchema.nullable(),
  /** More than fits: reload the latest page. */
  truncated: z.boolean(),
});
export type SyncConversationResponse = z.infer<typeof syncConversationResponseSchema>;

/**
 * POST /conversations/:conversationId/messages — send (or reply in a thread). Header
 * `Idempotency-Key` (UUID, required) becomes `clientMsgId`. Exactly one of `content` (the
 * composer's document) and `text` (token text, D-045) — `422 body_ambiguous` otherwise; either
 * may be empty only with `fileIds`. `201` new, `200` + `Idempotent-Replayed: true` on replay.
 */
export const sendMessageRequestSchema = z.strictObject({
  content: richTextDocWireSchema.optional(),
  text: z
    .string()
    .max(MESSAGING_LIMITS.maxPlainTextChars * 2)
    .optional(),
  threadRootId: uuidSchema.optional(),
  alsoSendToChannel: z.boolean().optional(),
  fileIds: z.array(uuidSchema).max(MESSAGING_LIMITS.maxFilesPerMessage).optional(),
  /** Required (true) when @here / @channel would notify more than the threshold. */
  confirmBroadcast: z.boolean().optional(),
});
export type SendMessageRequest = z.infer<typeof sendMessageRequestSchema>;

/** GET /messages/:messageId — resolve a permalink (conv-read). */
export const permalinkResponseSchema = z.object({
  message: messageDtoSchema,
  conversation: conversationDtoSchema,
  threadRootId: uuidSchema.nullable(),
});
export type PermalinkResponse = z.infer<typeof permalinkResponseSchema>;

/** PATCH /messages/:messageId — author only; files may be removed, not added. */
export const editMessageRequestSchema = z.strictObject({
  content: richTextDocWireSchema.optional(),
  text: z
    .string()
    .max(MESSAGING_LIMITS.maxPlainTextChars * 2)
    .optional(),
  fileIds: z.array(uuidSchema).max(MESSAGING_LIMITS.maxFilesPerMessage).optional(),
  /** Required when the edit adds @channel or @here above `messaging.broadcastWarnThreshold`. */
  confirmBroadcast: z.boolean().optional(),
});
export type EditMessageRequest = z.infer<typeof editMessageRequestSchema>;

/** DELETE /messages/:messageId — author, or admin+ (audited, D-046). */
export const deleteMessageRequestSchema = z.strictObject({
  reason: multiLineText(1, 500).optional(),
});
export type DeleteMessageRequest = z.infer<typeof deleteMessageRequestSchema>;

/** GET /admin/messages/:messageId/revisions — Owner only; every call is audited. */
export const messageRevisionDtoSchema = z.object({
  revision: z.number().int().positive(),
  action: z.enum(['edit', 'delete']),
  /** Null after the purge (`messaging.deletedContentRetentionDays`). */
  previousContent: richTextDocWireSchema.nullable(),
  previousPlainText: z.string().nullable(),
  /** Hex SHA-256 of the previous content, kept forever. */
  contentSha256: z.string(),
  actedBy: uuidSchema,
  actedAt: isoDateTimeSchema,
  purgeAfter: isoDateTimeSchema,
});
export type MessageRevisionDto = z.infer<typeof messageRevisionDtoSchema>;
export const messageRevisionsResponseSchema = z.object({
  items: z.array(messageRevisionDtoSchema),
});
export type MessageRevisionsResponse = z.infer<typeof messageRevisionsResponseSchema>;

/** GET /messages/:messageId/replies — the thread view (conv-read). */
export const threadRepliesQuerySchema = z.object({
  afterSeq: querySeq.optional(),
  beforeSeq: querySeq.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ThreadRepliesQuery = z.infer<typeof threadRepliesQuerySchema>;

export const threadRepliesResponseSchema = z.object({
  root: messageDtoSchema,
  /** Ascending seq. */
  items: z.array(messageDtoSchema),
  hasMoreBefore: z.boolean(),
  hasMoreAfter: z.boolean(),
});
export type ThreadRepliesResponse = z.infer<typeof threadRepliesResponseSchema>;

/** PUT /messages/:messageId/thread-subscription — follow or unfollow (conv-read). */
export const threadSubscriptionRequestSchema = z.strictObject({ following: z.boolean() });
export type ThreadSubscriptionRequest = z.infer<typeof threadSubscriptionRequestSchema>;
export const threadSubscriptionResponseSchema = z.object({ following: z.boolean() });

/** PUT/DELETE /messages/:messageId/reactions/:emoji — idempotent (conv-member). */
export const reactionsResponseSchema = z.object({
  reactions: z.array(reactionDtoSchema),
  version: z.number().int().positive(),
});
export type ReactionsResponse = z.infer<typeof reactionsResponseSchema>;

/** GET /messages/:messageId/reactions — who reacted (tooltip overflow). */
export const reactionListResponseSchema = z.object({
  items: z.array(z.object({ emoji: z.string(), userIds: z.array(uuidSchema) })),
});
export type ReactionListResponse = z.infer<typeof reactionListResponseSchema>;

// ================================================================ read state

/**
 * POST /conversations/:conversationId/read — mark read up to `lastReadSeq` (only forward), or one
 * thread when `threadRootId` is set. Also records a quick-switcher "recent".
 */
export const markReadRequestSchema = z.strictObject({
  lastReadSeq: seqSchema,
  threadRootId: uuidSchema.optional(),
});
export type MarkReadRequest = z.infer<typeof markReadRequestSchema>;

export const threadReadStateDtoSchema = z.object({
  threadRootId: uuidSchema,
  lastReadSeq: seqSchema,
});
export type ThreadReadStateDto = z.infer<typeof threadReadStateDtoSchema>;

export const markReadResponseSchema = z.union([memberStateDtoSchema, threadReadStateDtoSchema]);
export type MarkReadResponse = z.infer<typeof markReadResponseSchema>;

/** POST /conversations/:conversationId/unread — "mark unread from here" (lastReadSeq = fromSeq − 1). */
export const markUnreadRequestSchema = z.strictObject({
  fromSeq: z.number().int().positive(),
  threadRootId: uuidSchema.optional(),
});
export type MarkUnreadRequest = z.infer<typeof markUnreadRequestSchema>;

/** POST /conversations/read-all — Shift+Esc. */
export const readAllRequestSchema = z.strictObject({
  conversationIds: z.array(uuidSchema).max(500).optional(),
});
export type ReadAllRequest = z.infer<typeof readAllRequestSchema>;
export const readAllResponseSchema = z.object({ updatedCount: z.number().int().nonnegative() });

// ================================================================ presence

/** PUT /me/presence — manual away, or back to automatic. Live presence is on the socket. */
export const setPresenceRequestSchema = z.strictObject({ mode: presenceModeSchema });
export type SetPresenceRequest = z.infer<typeof setPresenceRequestSchema>;
export const presenceModeResponseSchema = z.object({ mode: presenceModeSchema });

/** GET /presence?userIds= — snapshot (guests: only people who share a conversation with them). */
export const presenceQuerySchema = z.object({ userIds: commaList(uuidSchema, { max: 200 }) });
export type PresenceQuery = z.infer<typeof presenceQuerySchema>;

export const presenceItemSchema = z.object({
  userId: uuidSchema,
  presence: presenceStateSchema,
  lastActiveAt: isoDateTimeSchema.nullable(),
  /** Phase 2: notifications paused (`dnd_until` in the future). Schedules are private. */
  dnd: z.boolean(),
  dndUntil: isoDateTimeSchema.nullable(),
  /** Phase 2: custom status; null when unset or expired. */
  status: z
    .object({
      emoji: z.string().nullable(),
      text: z.string().nullable(),
      expiresAt: isoDateTimeSchema.nullable(),
    })
    .nullable(),
});
export type PresenceItem = z.infer<typeof presenceItemSchema>;
export const presenceListResponseSchema = z.object({ items: z.array(presenceItemSchema) });
export type PresenceListResponse = z.infer<typeof presenceListResponseSchema>;

// ================================================================ conversation links

/**
 * GET /conversations/:conversationId/links — the conversation's Links tab (conv-read).
 *
 * The companion of the Files tab. A link is not a row anywhere: it lives inside a message body,
 * so the list is built by reading the messages flagged `has_link` (the partial index added in
 * 0026) and extracting their hrefs with the same `extractReferences` the send path uses to set
 * that flag. Title and site name are joined from `link_previews` where an unfurl happened, and
 * are null where it did not — unfurling is a workspace setting and may be off, so a tab that
 * showed only previewed links would be empty in exactly the workspaces that care most.
 *
 * Paging is by MESSAGE, not by link: the cursor is a message seq, and every link of the last
 * message on a page is on that page. A page can therefore carry slightly more than `limit`
 * items, which is the honest trade for never splitting one message's links across a boundary.
 */
export const conversationLinksQuerySchema = paginationQuerySchema;
export type ConversationLinksQuery = z.infer<typeof conversationLinksQuerySchema>;

export const conversationLinkItemSchema = z.object({
  /** The href as written, after `extractReferences` strips trailing punctuation from bare URLs. */
  url: z.string(),
  messageId: uuidSchema,
  sharedAt: isoDateTimeSchema,
  sharedBy: uuidSchema,
  /** From `link_previews`, or null when nothing unfurled this URL. */
  title: z.string().nullable(),
  siteName: z.string().nullable(),
});
export type ConversationLinkItem = z.infer<typeof conversationLinkItemSchema>;

export const conversationLinksResponseSchema = paginated(conversationLinkItemSchema);
export type ConversationLinksResponse = z.infer<typeof conversationLinksResponseSchema>;

// ================================================================ the conversation canvas

/**
 * `GET`/`PUT /conversations/:conversationId/canvas` (D-1157) — one rich-text document per
 * conversation: the notes a channel keeps beside its messages.
 *
 * Reading follows the conversation (conv-read → 404). Writing follows POSTING: if the channel is
 * `admins_only` (D-1144) then only the people who may post may edit the canvas, because a
 * document everybody can rewrite in a channel only admins may speak in is a hole in the same
 * rule.
 *
 * `version` is optimistic concurrency, not a revision history: an edit sends the version it read
 * and a stale one is refused (`409 canvas_version_conflict`) rather than overwriting somebody.
 * Two carets at once needs a CRDT and a different transport; refusing is honest where silent
 * clobbering is not.
 */
export const canvasDtoSchema = z.object({
  conversationId: uuidSchema,
  /** `null` until somebody writes one — a conversation always HAS a canvas, it is just empty. */
  body: richTextDocWireSchema.nullable(),
  bodyText: z.string(),
  version: z.number().int().positive(),
  updatedByUserId: uuidSchema.nullable(),
  updatedAt: isoDateTimeSchema.nullable(),
  /** Whether this viewer may edit it (posting rights, D-1144). */
  canEdit: z.boolean(),
});
export type CanvasDto = z.infer<typeof canvasDtoSchema>;

export const putCanvasRequestSchema = z.strictObject({
  body: richTextDocWireSchema,
  /** The version the editor read. An unwritten canvas reads 1, so this is never 0. */
  version: z.number().int().positive(),
});
export type PutCanvasRequest = z.infer<typeof putCanvasRequestSchema>;
